import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Sparkles, Loader2, Pencil, Merge, Check, X, Globe2, Lock } from 'lucide-react';

const chip = 'px-1.5 py-0.5 rounded text-[10px]';

const CompanyRow = ({ c, all, api, onChanged, setNotice }) => {
    const [edit, setEdit] = useState(false);
    const [name, setName] = useState(c.name);
    const [domains, setDomains] = useState(c.domains.join(', '));
    const [mergeTo, setMergeTo] = useState('');
    const [err, setErr] = useState('');
    const save = async () => {
        setErr('');
        const r = await api(`/api/v1/admin/spark/companies/${c.id}`, { method: 'PUT', body: JSON.stringify({ name, domains: domains.split(/[\s,;]+/).filter(Boolean) }) });
        if (r.ok) { setEdit(false); setNotice(`"${name}" aggiornata.`); onChanged(); } else setErr(r.data.detail || 'Errore');
    };
    const merge = async () => {
        const t = all.find(x => x.id === mergeTo);
        if (!t || !window.confirm(`Unire "${c.name}" in "${t.name}"? Tutti gli Spark di "${c.name}" passeranno a "${t.name}".`)) return;
        const r = await api('/api/v1/admin/spark/companies/merge', { method: 'POST', body: JSON.stringify({ source_id: c.id, target_id: mergeTo }) });
        if (r.ok) { setNotice(`"${c.name}" unita in "${t.name}".`); onChanged(); } else setErr(r.data.detail || 'Errore');
    };
    return (
        <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2" data-testid={`company-row-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    {edit ? (
                        <input value={name} onChange={e => setName(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-white" data-testid={`company-name-input-${c.id}`} />
                    ) : (
                        <p className="text-sm font-semibold text-white flex items-center gap-2" data-testid={`company-name-${c.id}`}>
                            <Building2 className="w-4 h-4 text-indigo-300" />{c.name}
                            <span className="text-xs font-normal text-slate-500">{c.spark_count} Spark</span>
                            {c.source === 'ai' && <span className={`${chip} bg-fuchsia-500/15 text-fuchsia-300 flex items-center gap-1`}><Sparkles className="w-3 h-3" />creata da AI</span>}
                            {c.locked && <span className={`${chip} bg-slate-700 text-slate-300 flex items-center gap-1`} title="Modificata a mano: l'AI non la cambia"><Lock className="w-3 h-3" />manuale</span>}
                        </p>
                    )}
                    {c.aliases.length > 0 && <p className="text-[11px] text-slate-500 mt-1">Nomi uniti: {c.aliases.join(' · ')}</p>}
                </div>
                <div className="flex gap-1.5">
                    {edit ? (
                        <>
                            <button onClick={save} className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`company-save-${c.id}`}><Check className="w-4 h-4" /></button>
                            <button onClick={() => { setEdit(false); setName(c.name); setDomains(c.domains.join(', ')); }} className="p-1.5 rounded-lg border border-slate-700 text-slate-300" data-testid={`company-cancel-${c.id}`}><X className="w-4 h-4" /></button>
                        </>
                    ) : (
                        <button onClick={() => setEdit(true)} className="px-2.5 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs flex items-center gap-1" data-testid={`company-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /> Modifica</button>
                    )}
                </div>
            </div>
            {edit ? (
                <input value={domains} onChange={e => setDomains(e.target.value)} placeholder="domini separati da virgola, es. standgreen.com"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white" data-testid={`company-domains-input-${c.id}`} />
            ) : (
                <div className="flex flex-wrap gap-1">
                    {c.domains.length === 0 && <span className="text-[11px] text-slate-600">Nessun dominio collegato</span>}
                    {c.domains.map(d => <span key={d} className={`${chip} bg-sky-500/15 text-sky-300 flex items-center gap-1`}><Globe2 className="w-3 h-3" />@{d}</span>)}
                </div>
            )}
            {!edit && (
                <div className="flex items-center gap-2">
                    <select value={mergeTo} onChange={e => setMergeTo(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-xs text-slate-300" data-testid={`company-merge-select-${c.id}`}>
                        <option value="">Unisci in…</option>
                        {all.filter(x => x.id !== c.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                    {mergeTo && <button onClick={merge} className="px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs flex items-center gap-1" data-testid={`company-merge-btn-${c.id}`}><Merge className="w-3.5 h-3.5" /> Unisci</button>}
                </div>
            )}
            {err && <p className="text-xs text-red-400" data-testid={`company-error-${c.id}`}>{err}</p>}
        </div>
    );
};

export const CompaniesPanel = ({ api, onChanged }) => {
    const [list, setList] = useState([]);
    const [aiAt, setAiAt] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const load = useCallback(async () => {
        const r = await api('/api/v1/admin/spark/companies');
        if (r.ok) { setList(r.data.companies || []); setAiAt(r.data.ai_at); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const changed = () => { load(); onChanged(); setTimeout(() => setNotice(''), 4000); };
    const rebuild = async () => {
        setBusy(true);
        const r = await api('/api/v1/admin/spark/companies/rebuild?force_ai=true', { method: 'POST' });
        setBusy(false);
        if (r.ok && !r.data.ai) setNotice(`Ricalcolo fatto senza AI (Gemini non ha risposto): ${r.data.companies} aziende, ${r.data.spark_updated} Spark aggiornati.`);
        else if (r.ok) setNotice(`AI completata: ${r.data.companies} aziende, ${r.data.domains_assigned} domini collegati, ${r.data.spark_updated} Spark aggiornati.`);
        else setNotice(r.data.detail || 'Ricalcolo non riuscito.');
        changed();
    };
    return (
        <div className="space-y-3" data-testid="companies-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-slate-500">Le aziende sono unite per nome e riconosciute dal dominio email (se combacia con l'azienda del PC). L'AI collega i domini restanti e unisce i doppioni. {aiAt && `Ultima analisi AI: ${new Date(aiAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}.`}</p>
                <button onClick={rebuild} disabled={busy} className="px-3 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm flex items-center gap-2 disabled:opacity-60" data-testid="companies-rebuild-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Ricalcola con AI
                </button>
            </div>
            {notice && <p className="text-sm text-emerald-300" data-testid="companies-notice">{notice}</p>}
            {list.length === 0 && <p className="text-sm text-slate-500">Nessuna azienda ancora.</p>}
            {list.map(c => <CompanyRow key={c.id} c={c} all={list} api={api} onChanged={changed} setNotice={setNotice} />)}
        </div>
    );
};

export default CompaniesPanel;
